import { useEffect } from "react";
import { useEtlynCms } from "@etlyn/etlyn-cms";
import { getCompanyId } from "../../companyContext";
import { readSiteDefaults, readSiteBootstrap } from "./siteDefaults";

const SITE_NAME = "Etlyn";
const BASE_URL = "https://www.etlyn.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

interface SEOHeadProps {
  /** Page title — will be suffixed with " | Etlyn" unless `fullTitle` is true */
  title: string;
  /** Meta description — keep between 120-160 chars */
  description: string;
  /** Canonical path, e.g. "/about" — auto-prefixed with base URL */
  path?: string;
  /** Open Graph image URL */
  image?: string;
  /** OG type — defaults to "website" */
  type?: "website" | "article";
  /** If true, use `title` exactly as-is without appending site name */
  fullTitle?: boolean;
  useSiteDefaults?: boolean;
  /** Article publish date (ISO string) — for blog posts */
  publishedTime?: string;
  modifiedTime?: string;
  robots?: string;
  /** Article author */
  author?: string;
  /** Additional keywords for this page */
  keywords?: string;
  /** JSON-LD structured data */
  jsonLd?: Record<string, unknown>;
}

/**
 * SEOHead — lightweight document head manager for SPA SEO.
 *
 * Updates document.title and meta/OG tags on mount. Restores defaults
 * on unmount so navigating away doesn't leave stale tags.
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  fullTitle = false,
  useSiteDefaults = false,
  publishedTime,
  modifiedTime,
  robots = "index,follow",
  author,
  keywords,
  jsonLd,
}) => {
  const {state} = useEtlynCms("company-info");
  const publicId=getCompanyId();
  const defaults=readSiteDefaults(state.data?.id===publicId ? state.data : readSiteBootstrap(publicId));
  const siteName=defaults.siteName || SITE_NAME;
  title=useSiteDefaults ? defaults.title || title : title;
  description=useSiteDefaults ? defaults.description || description : description;
  image=image || defaults.image || DEFAULT_OG_IMAGE;
  const pageTitle = fullTitle || (useSiteDefaults && Boolean(defaults.title)) ? title : `${title} | ${siteName}`;
  const canonicalUrl = `${BASE_URL}${path}`;

  useEffect(() => {
    // Title
    document.title = pageTitle;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content?: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!content) { el?.remove(); return; }
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Primary meta
    setMeta("name", "title", pageTitle);
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("name", "keywords", keywords);
    setMeta("name", "author", author);

    // Open Graph
    setMeta("property", "og:title", pageTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", image);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", siteName);

    // Twitter
    setMeta("name", "twitter:title", pageTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:url", canonicalUrl);
    setMeta("name", "twitter:image", image);

    // Article-specific
    setMeta("property", "article:published_time", type === "article" ? publishedTime : undefined);
    setMeta("property", "article:modified_time", type === "article" ? modifiedTime : undefined);
    setMeta("property", "article:author", type === "article" ? author : undefined);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // JSON-LD structured data
    document.getElementById("seo-page-jsonld")?.remove();
    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.id = "seo-page-jsonld";
      jsonLdScript.textContent = JSON.stringify(jsonLd);
      // Remove existing page-level JSON-LD first
      const existing = document.getElementById("seo-page-jsonld");
      if (existing) existing.remove();
      document.head.appendChild(jsonLdScript);
    }

    // Cleanup on unmount — restore defaults
    return () => {
      document.title = `${SITE_NAME} — Software Development for Growing Businesses`;
      if (jsonLdScript) jsonLdScript.remove();
      for (const key of ["article:published_time", "article:modified_time", "article:author"])
        setMeta("property", key);
      setMeta("name", "keywords");
      setMeta("name", "author");
    };
  }, [pageTitle, description, canonicalUrl, image, siteName, type, publishedTime, modifiedTime, robots, author, keywords, jsonLd]);

  return null;
};

export default SEOHead;
